import { View, ViewStyle } from 'react-native'
import { createComponent } from 'react-native-small-ui'
import { formatBlindTimerMs } from '@/hooks/convex/useBlindTimer'
import { usePlayerTimer } from '@/hooks/convex/usePlayerTimer'
import { DS } from '../../../design-system'
import Animated, { useAnimatedStyle, useDerivedValue } from 'react-native-reanimated'
import { useVibrationAndSounds } from '../../../screens/new-design/hooks/useVibrationAndSounds'
import { useGameState } from '@/screens/GameContainer/useGameState'
import { translate } from '../../../i18n/translate'
import { Avatar } from '@/screens/profile-screen/edit/AvatarBuilder'

const Box = createComponent(View, {
  flexGrow: 1
})
const Row = createComponent(View, {
  flexDirection: 'row'
})

const Center = createComponent(View, {
  justifyContent: 'center',
  alignItems: 'center'
})

type PlayerTimerProps = {
  containerStyle?: ViewStyle
}

export const PlayerTimer = ({ containerStyle }: PlayerTimerProps) => {
  const timerData = usePlayerTimer()
  const { myPlayer, isMyTurn } = useGameState()

  if (isMyTurn && timerData?.remaining) {
    return (
      <Center flexGrow={1} style={containerStyle}>
        <DS.TextScaled variant={'Caption/Bold'} color={'Global/Black'} uppercase>
          {translate('newGameScreen.common.yourTurn')}
        </DS.TextScaled>
        <DS.TextScaled variant={'Mini/Medium'} color={'Global/Black'} uppercase>
          {formatBlindTimerMs(timerData.remaining)}
        </DS.TextScaled>
      </Center>
    )
  }

  if (myPlayer?.name) {
    return (
      <Center flexGrow={1} style={containerStyle}>
        <Avatar size={24}/>
        <View style={{position: "relative", top: 8}}>        
          <DS.TextScaled variant={'Caption/Bold'} color={'Global/Black'} numberOfLines={1}>
            {myPlayer.name}
          </DS.TextScaled>
        </View>
      </Center>
    )
  }

  return null
}

export const PlayerProgressBar = ({ containerStyle }: PlayerTimerProps) => {
  const timerData = usePlayerTimer()

  useVibrationAndSounds({ remaining: timerData?.remaining ?? 0 })

  const progress = useDerivedValue(() => {
    return timerData?.progress
  }, [timerData?.progress])

  const barHeight = 5

  const animatedStyles = useAnimatedStyle(() => {
    const v = (progress?.value ?? 0) * 100
    const color = v > 35 ? DS.palette['Brand/Orange'] : DS.palette['Brand/Pink']
    return {
      backgroundColor: color,
      width: `${v}%`
    }
  }, [progress])

  if (!timerData) {
    return null
  }

  return (
    <Row paddingHorizontal={0} position="absolute" marginTop={-5} width={'100%'} style={containerStyle}>
      <Box height={barHeight} backgroundColor={'#0002'}>
        <Animated.View style={[animatedStyles, { height: barHeight }]} />
      </Box>
    </Row>
  )
}
